import { usePageDrawer } from '@app/components/page/drawer/PageDrawerContext';
import { useEffect, useRef } from 'react';
import {
  AutoOpenFragmentMode,
  useStreamSettingsStore,
} from '../../../stores/stream-screen-settings.store';
import { useStreamSelection } from '../../../stores/useStreamSelection';
import { StreamMode } from '../../../types';
import { useOpenFragmentDrawer } from './useOpenFragmentDrawer';

/**
 * Hook that automatically opens the fragment drawer when a selection marker appears.
 * It tracks when tsA or tsB changes to a new value and opens the drawer once per selection.
 * The behavior is controlled by the autoOpenFragmentMode setting.
 */
export function useAutoOpenFragmentDrawer() {
  const { selection } = useStreamSelection();
  const { tsA, tsB } = selection;
  const { open: drawerOpen } = usePageDrawer();
  const openFragmentDrawer = useOpenFragmentDrawer();
  const autoOpenFragmentMode = useStreamSettingsStore((state) => state.autoOpenFragmentMode);
  const mode = useStreamSettingsStore((state) => state.mode);
  const hasOpenedDrawerRef = useRef(false);
  const previousTsA = useRef<number | null>(null);
  const previousTsB = useRef<number | null>(null);

  useEffect(() => {
    // Auto-open is only active when editor mode is DEFAULT
    if (mode !== StreamMode.DEFAULT) {
      return;
    }

    // If auto-open is disabled, don't do anything
    if (autoOpenFragmentMode === AutoOpenFragmentMode.NEVER) {
      return;
    }

    // Detect when tsA or tsB changes to a different value (new selection start/end)
    const tsAChanged = tsA !== null && tsA !== previousTsA.current;
    const tsBChanged = tsB !== null && tsB !== previousTsB.current;

    let shouldOpen = false;

    if (autoOpenFragmentMode === AutoOpenFragmentMode.ON_START_OR_END) {
      // Open when either start or end is marked
      shouldOpen = tsAChanged || tsBChanged;
    } else if (autoOpenFragmentMode === AutoOpenFragmentMode.ON_START_AND_END) {
      // Open only when both start and end are marked
      shouldOpen = tsA !== null && tsB !== null && (tsAChanged || tsBChanged);
    }

    // Reset the ref when selection is cleared OR when a new selection starts (value changes)
    if (!tsA && !tsB) {
      hasOpenedDrawerRef.current = false;
    } else if (shouldOpen) {
      // Reset when a new marker appears (new selection start/end)
      hasOpenedDrawerRef.current = false;
    }

    // Open drawer when condition is met and we haven't opened it yet for this selection
    if (shouldOpen && !hasOpenedDrawerRef.current && !drawerOpen) {
      hasOpenedDrawerRef.current = true;
      openFragmentDrawer();
    }

    previousTsA.current = tsA;
    previousTsB.current = tsB;
  }, [tsA, tsB, drawerOpen, openFragmentDrawer, autoOpenFragmentMode, mode]);
}
